/**
 * We only use this cache as fallback when Amplitude is offline,
 * or when experiment client initializes, but no flags are returned from the server
 *
 * It may also be consumed downstream by snowplow context. See app/javascript/modules/analytics/helpers.ts:attachExperimentsContext
 *
 * It only stores variants when there is an assignment or offline fallback argument
 *
 * Values are stored 24 hrs in localStorage
 *
 */

import { Variant, Variants } from '@amplitude/experiment-js-client';

import CacheSession from './CacheSession';

// Key where we save the latests assignation of experiments for the user
export const variantCacheKey = '__wt-experiment-variants';

const resetCache = (): void => {
  localStorage.removeItem(variantCacheKey);
};

export const getVariantCache = (): Variants => {
  if (!window.localStorage) {
    return {};
  }

  if (CacheSession.isCacheExpired()) {
    resetCache();
  }

  try {
    const values = localStorage.getItem(variantCacheKey);

    return values ? JSON.parse(atob(values)) : {};
  } catch (error) {
    // If storage fails to return the last saved value we MUST recover from that state.
    return {};
  }
};

export const setCachedVariant = (key: string, variant: Variant): void => {
  if (
    !window.localStorage ||
    variant?.value === undefined ||
    variant?.value === ''
  ) {
    return;
  }

  const variants = getVariantCache();

  variants[key] = variant;

  localStorage.setItem(variantCacheKey, btoa(JSON.stringify(variants)));

  CacheSession.setCacheTimestamp();
};
